import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { cn } from '@/lib/utils';
import { CopilotAssistant } from '@/hooks/ai-agents/types';
import { logUiEvent } from '@/utils/logger';

interface AssistantCardProps {
  assistant: CopilotAssistant;
  isActive: boolean;
  onClick: (assistantId: string) => void;
}

const AssistantCard: React.FC<AssistantCardProps> = ({
  assistant,
  isActive,
  onClick,
}) => {
  const handleClick = () => {
    onClick(assistant.id);
  };

  return (
    <motion.div
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
      className={cn(
        "relative cursor-pointer rounded-xl overflow-hidden group",
        "bg-gradient-to-b from-white to-slate-50 dark:from-slate-800 dark:to-slate-900",
        "border transition-all duration-300 ease-out shadow-sm",
        isActive
          ? "ring-2 ring-primary ring-offset-2 dark:ring-offset-slate-950 border-transparent shadow-lg" 
          : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md"
      )}
    >
      {/* Hidden radio input for accessibility */}
      <input 
        type="radio" 
        className="sr-only" 
        name="assistant"
        value={assistant.id}
        checked={isActive}
        onChange={() => {}}
        aria-label={`Select ${assistant.name} assistant`}
      />
      
      <div className="relative">
        {/* Image container with fixed dimensions and gradient overlay */}
        <div className="aspect-square w-full overflow-hidden">
          {assistant.profile_picture_url ? (
            <OptimizedImage 
              src={assistant.profile_picture_url} 
              alt={assistant.name} 
              className={cn(
                "w-full h-full object-cover transition-transform duration-500 group-hover:scale-105",
              )}
              onError={(e) => {
                logUiEvent('AssistantSelection', 'assistant image error', { assistantId: assistant.id });
                console.log(`Image error for assistant ${assistant.name}:`, e);
                (e.target as HTMLImageElement).src = '/placeholder.svg';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30">
              <Avatar className="w-24 h-24">
                <AvatarFallback className="text-2xl font-medium bg-primary/10 text-primary">
                  {assistant.name.substring(0, 2)}
                </AvatarFallback>
              </Avatar>
            </div>
          )}
          
          {/* Gradient overlay - concentrated in the bottom left corner */}
          <div className={cn(
            "absolute inset-0 transition-opacity duration-300",
            "group-hover:opacity-80"
          )} 
            style={{
              background: `
                radial-gradient(circle at bottom left, rgba(15, 23, 42, 0.75) 0%, rgba(15, 23, 42, 0.4) 15%, rgba(15, 23, 42, 0.05) 40%, transparent 70%),
                linear-gradient(to top right, rgba(15, 23, 42, 0.5) 0%, transparent 50%)
              `
            }}
          />
        </div>
        
        {/* Job/Specialization pill - top left corner */}
        {assistant.specialization && (
          <motion.div 
            className="absolute top-3 left-3 z-30"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className={cn(
              "px-2.5 py-1 rounded-full text-xs font-medium",
              "bg-white/20 dark:bg-slate-900/30 backdrop-blur-md",
              "border border-white/30 dark:border-slate-700/50",
              "text-white shadow-lg",
              "transition-all duration-300 group-hover:bg-black/40 group-hover:backdrop-blur-lg dark:group-hover:bg-black/50"
            )}>
              {assistant.specialization}
            </div>
          </motion.div>
        )}
        
        {/* Name - bottom left corner */}
        <div className={cn(
          "absolute bottom-3 left-3 z-10",
        )}>
          <h3 className={cn(
            "font-medium text-base text-white",
            "drop-shadow-sm"
          )}>
            {assistant.name}
          </h3>
        </div>
        
        {/* Selection indicator overlay */}
        {isActive && (
          <div className="absolute inset-0 bg-primary/10 dark:bg-primary/20 backdrop-blur-[1px] z-5" />
        )}
      </div>
      
      {/* Selected indicator badge with animation */}
      <AnimatedCheckmark isActive={isActive} />
    </motion.div>
  );
};

// Animated checkmark component for selection indicator
interface AnimatedCheckmarkProps {
  isActive: boolean;
}

const AnimatedCheckmark: React.FC<AnimatedCheckmarkProps> = ({ isActive }) => {
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div 
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center shadow-md z-20"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: 1, 
            opacity: 1,
            transition: { 
              type: "spring", 
              stiffness: 260, 
              damping: 20,
              duration: 0.4 
            }
          }}
          exit={{ 
            scale: 0, 
            opacity: 0,
            transition: { 
              duration: 0.3,
              ease: "easeOut" 
            }
          }}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="3" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            className="w-4 h-4 text-white"
          >
            <motion.polyline 
              points="20 6 9 17 4 12"
              initial={{ pathLength: 0 }}
              animate={{ 
                pathLength: 1,
                transition: { 
                  delay: 0.15, 
                  duration: 0.5, 
                  ease: "easeInOut" 
                }
              }}
            />
          </svg>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AssistantCard;
